import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { ProductComponent } from '@theme/view-event-elements';
import { debounce, isDesktopBreakpoint, mediaQueryLarge } from '@theme/utilities';
import { morph } from '@theme/morph';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * How long the pointer must stay on a card before work that only a real visit needs is started.
 * A sweep across a row must not pay for cards it merely crossed.
 */
const HOVER_INTENT_DELAY = 120;

/**
 * Writes the material line without rebuilding its text node.
 *
 * `el.textContent = x` removes the old text node and creates a new one even when the string is
 * identical — a structural DOM change, and every structural change re-runs the theme's `:has()`
 * matching. On a collection page that measured 71-84ms per write on live /collections/auskari,
 * and the reset half of a swatch hover ran it on EVERY pointerleave: `data-materials` is empty on
 * these variants, so `#updateMaterialLine` fell through to `data-default` and rewrote the exact
 * string that was already on screen. Skipping the equal write costs 0; mutating the existing text
 * node in place (for variants that really do differ) measured ~2ms instead of ~80.
 * @param {Element} el - The material line element.
 * @param {string} text - The text to show.
 */
function setMaterialText(el, text) {
  if (el.textContent === text) return;

  const [first] = el.childNodes;
  if (el.childNodes.length === 1 && first instanceof Text) {
    first.data = text;
  } else {
    el.textContent = text;
  }
}

/**
 * @typedef {object} ProductCardLinkRefs
 * @property {HTMLElement} [cardGallery] - The card gallery element.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 */

/**
 * A custom element for product links with images for transitions to PDP.
 * This is a base class that is extended by ProductCard.
 * Used directly by resource-card.liquid for non-product-card scenarios.
 * Extends ProductComponent to automatically emit product:view events when visible.
 *
 * @template {ProductCardLinkRefs} [T=ProductCardLinkRefs]
 * @extends {ProductComponent<T>}
 */
export class ProductCardLink extends ProductComponent {
  get productTransitionEnabled() {
    return this.getAttribute('data-product-transition') === 'true';
  }

  get featuredMediaUrl() {
    return this.getAttribute('data-featured-media-url');
  }

  /**
   * Handles the click event for view transitions.
   * @param {Event} event
   */
  handleViewTransition(event) {
    // If the event has been prevented, don't do anything, another component is handling the click
    if (event.defaultPrevented) return;

    // If the event was on an interactive element, don't do anything, this is not a navigation
    if (event.target instanceof Element) {
      const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');
      if (interactiveElement) return;
    }

    if (!this.productTransitionEnabled) return;

    const { cardGallery } = this.refs;
    if (!cardGallery || !cardGallery.hasAttribute('data-view-transition-to-main-product')) return;

    // Check on the current active image, whether it's a product card image or a resource card image
    const { imagesToTransition } = this.refs;
    const activeImage =
      imagesToTransition?.find(
        (/** @type {HTMLImageElement} */ image) =>
          image.closest('slideshow-slide')?.getAttribute('aria-hidden') === 'false'
      ) || imagesToTransition?.[imagesToTransition.length - 1];

    if (activeImage instanceof HTMLImageElement) this.#setImageSrcset(activeImage);

    cardGallery.setAttribute('data-view-transition-type', 'product-image-transition');
    cardGallery.setAttribute('data-view-transition-triggered', 'true');
  }

  /**
   * Sets the srcset for the image
   * @param {HTMLImageElement} image
   */
  #setImageSrcset(image) {
    if (!this.featuredMediaUrl) return;
    // A card image parked by r69-lazy.js (data-src, not yet near the viewport) has no currentSrc,
    // and new URL('') throws inside the click handler. Nothing to transition from — skip.
    if (!image.currentSrc) return;

    const currentImageUrl = new URL(image.currentSrc);

    // Deliberately not using origin, as it includes the protocol, which is usually skipped for featured media
    const currentImageRawUrl = currentImageUrl.host + currentImageUrl.pathname;

    if (!this.featuredMediaUrl.includes(currentImageRawUrl)) {
      const imageFade = image.animate([{ opacity: 0.8 }, { opacity: 1 }], {
        duration: 125,
        easing: 'ease-in-out',
      });

      imageFade.onfinish = () => {
        image.srcset = this.featuredMediaUrl ?? '';
      };
    }
  }
}

if (!customElements.get('product-card-link')) {
  customElements.define('product-card-link', ProductCardLink);
}

/**
 * A custom element that displays a product card.
 * Extends ProductCardLink to inherit view transition functionality.
 *
 * @typedef {object} ProductCardRefs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 * @extends {ProductCardLink<ProductCardRefs>}
 */
export class ProductCard extends ProductCardLink {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #prefetchTimer;

  /**
   * Holds the quick-add prefetch until the pointer has actually settled on this card.
   *
   * The prefetch pulls a WHOLE product page and DOMParses it: measured on live
   * /collections/auskari?page=3 at 1 080 647 bytes, 846ms of network and 18.6ms of parsing for
   * 4 061 elements — per card. Bound straight to `pointerenter`, one sweep across a row fired it for
   * every card crossed, so the main thread got a chain of parses and the network a burst of
   * megabyte responses while the user was still moving. A pointer that is passing through is not
   * intent; one that stops for 120ms is.
   */
  #queueProductPagePrefetch = () => {
    clearTimeout(this.#prefetchTimer);
    this.#prefetchTimer = setTimeout(this.#fetchProductPageHandler, HOVER_INTENT_DELAY);
  };

  #cancelProductPagePrefetch = () => {
    clearTimeout(this.#prefetchTimer);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });

    this.#keepSelectedSlideVisible();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
    this.#selectedSlideObserver?.disconnect();
    this.#selectedSlideObserver = null;
    this.removeEventListener('pointerleave', this.#revealSelectedSlide);
    this.#cancelProductPagePrefetch();
  }

  /** @type {MutationObserver | null} */
  #selectedSlideObserver = null;

  /**
   * Ensure the selected variant's slide stays revealed while the card is at rest.
   *
   * When the Featured collection section uses the "Carousel" layout, each product
   * card is nested inside the section's slideshow, so the card's own gallery
   * slideshow runs in `disabled` mode. A disabled gallery never gets
   * `[in-viewport]`/`[actioned]` and relies solely on the server-rendered
   * `aria-hidden="false"` of its first slide. If the slideshow's init race flips
   * that slide to `aria-hidden="true"` it becomes `content-visibility: hidden`
   * (a blank gray card) with nothing to recover it. We re-assert the selected
   * slide here. (Grid/Editorial don't nest the gallery, so they self-recover.)
   */
  #revealSelectedSlide = () => {
    const slideshow = this.querySelector('slideshow-component');
    if (!slideshow) return;
    // While hovered the gallery legitimately swaps slides (second-image-on-hover
    // and swatch preview); only enforce the resting state so we don't fight it.
    if (this.matches(':hover')) return;

    const id = this.variantPicker?.selectedOption?.dataset.optionMediaId;
    const target = id
      ? slideshow.querySelector(`slideshow-slide[slide-id="${id}"]`)
      : slideshow.querySelector('slideshow-slide');
    if (target instanceof HTMLElement && target.getAttribute('aria-hidden') !== 'false') {
      target.removeAttribute('hidden');
      target.setAttribute('aria-hidden', 'false');
    }
  };

  #keepSelectedSlideVisible() {
    // Defer so the slideshow and its slides are parsed.
    requestAnimationFrame(() => {
      const slideshow = this.querySelector('slideshow-component');
      if (!slideshow) return;
      // Only galleries that rest on a single image (product image carousel off,
      // incl. when nested in a section carousel) hit this bug. Leave interactive
      // multi-image card carousels alone.
      if (slideshow.getAttribute('disabled') !== 'true') return;

      // React to the slideshow's own (possibly late, async) aria-hidden writes...
      this.#selectedSlideObserver = new MutationObserver(this.#revealSelectedSlide);
      this.#selectedSlideObserver.observe(slideshow, {
        subtree: true,
        attributes: true,
        attributeFilter: ['aria-hidden'],
      });

      // ...and re-assert directly, since a write may land while hovered (skipped
      // above) and leave no later mutation to react to.
      this.#revealSelectedSlide();
      setTimeout(this.#revealSelectedSlide, 100);
      setTimeout(this.#revealSelectedSlide, 400);
      this.addEventListener('pointerleave', this.#revealSelectedSlide);
    });
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    const img = currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]');
    if (!img) return;
    img.removeAttribute('loading');
    // Home-page cards keep their src in data-src (r69-lazy.js); hand the swap over so the hover
    // photo is ready at once, as on every other page.
    if (img.classList.contains('r69-lz') && window.r69Lazy) window.r69Lazy.load(img);
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.#cancelProductPagePrefetch();
    this.removeEventListener('pointerenter', this.#queueProductPagePrefetch);
    this.removeEventListener('pointerleave', this.#cancelProductPagePrefetch);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#queueProductPagePrefetch);
      this.addEventListener('pointerleave', this.#cancelProductPagePrefetch);
      // Keyboard focus is a deliberate landing, not a fly-by — fetch straight away.
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the product select event (variant selected and updated).
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    // Update variant picker when variant:selected event fires
    const { optionValueId } = event.detail ?? {};
    if (optionValueId && event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(optionValueId);
    }

    // Wait for variant:update data via promise
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;

        // The freshly rendered price is the new baseline — drop hover-preview
        // caches so a later pointerleave can't restore the pre-click price.
        this.originalPriceHTML = null;
        this.fetchingPreviewVariantId = null;

        // Update price, availability, and URL based on new variant
        this.updatePrice(html);
        this.#isUnavailableVariantSelected(html);
        this.#updateProductUrl(html);
        this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

        if (event.target !== this.variantPicker) {
          this.variantPicker?.updateVariantPicker(html);
        }

        this.#updateVariantImages();
        this.#updateMaterialLine();

        // Remove attribute after re-rendering since a variant selection has been made
        this.removeAttribute('data-no-swatch-selected');

        // Force overflow list to reflow after variant update
        this.#updateOverflowList();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-card] Event promise rejected:', error);
      });
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {Document} html - The parsed HTML document with updated variant data.
   */
  updatePrice(html) {
    // Desktop cards render two price containers (card + quick add), mobile only
    // one — fall back to the single container so mobile prices still update.
    const priceContainer =
      this.querySelectorAll(`product-price [ref='priceContainer']`)[1] ||
      this.querySelector(`product-price [ref='priceContainer']`);
    const newPriceElement = html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update.
   * @param {Document} html - The parsed HTML document with updated variant data.
   */
  #updateProductUrl(html) {
    const responseProductCard = html.querySelector('product-card');
    const anchorElement = responseProductCard?.querySelector('a');
    const featuredMediaUrl = responseProductCard?.getAttribute('data-featured-media-url');

    // Update the featured media URL for view transitions (inherited from ProductCardLink)
    if (featuredMediaUrl) {
      this.setAttribute('data-featured-media-url', featuredMediaUrl);
    }

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      // Раньше здесь ссылка заменялась целиком на серверную. Ответ Section Rendering не знает
      // ничего о том, откуда покупатель пришёл, поэтому вместе со ссылкой стирались параметры
      // атрибуции: на выдаче поиска это `_pos`, `_sid`, `_ss`, по которым Shopify связывает
      // поиск с товаром. Замерено на /search?q=auskari: клик по свотчу превращал
      // `/products/auskari-286?variant=…&_pos=4&_sid=…&_ss=r` в голый абсолютный URL.
      // Берём из ответа только ПУТЬ и `variant`, всё остальное у каждой ссылки оставляем своё.
      const responseUrl = new URL(anchorElement.href);
      this.#updateLinks({
        pathname: responseUrl.pathname,
        variantId: responseUrl.searchParams.get('variant'),
      });
    }
  }

  /**
   * Переписывает все три ссылки карточки на месте, сохраняя их собственные query-параметры.
   * @param {object} options
   * @param {string} [options.pathname] - Новый путь. Без него путь каждой ссылки остаётся своим.
   * @param {string | null} [options.variantId] - Вариант. null/undefined убирает параметр.
   */
  #updateLinks({ pathname, variantId }) {
    const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

    for (const linkEl of [productCardLink, cardGalleryLink, productTitleLink]) {
      if (!(linkEl instanceof HTMLAnchorElement) || !linkEl.href) continue;

      const url = new URL(linkEl.href);
      if (pathname) url.pathname = pathname;
      if (variantId) url.searchParams.set('variant', variantId);
      else url.searchParams.delete('variant');
      linkEl.href = url.toString();
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {Document} html - The parsed HTML document with updated variant data.
   */
  #isUnavailableVariantSelected(html) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (html.querySelectorAll('input:checked'));

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Shows the image group belonging to an option value and hides the rest.
   *
   * A slide belongs to a group either natively (`variant-image`, one image per
   * variant) or via our alt-text tagging (`data-variant-option-value`, several
   * images per variant, matched case-insensitively). Untagged slides are shared
   * and stay visible. If no slide is tagged for the value, falls back to the
   * stock single-image behavior keyed by `slide-id`.
   * @param {string | undefined} optionValue - The option value (e.g. color name) to show images for.
   * @param {string | undefined} defaultImageId - The media id of the value's featured image.
   */
  #showImagesForVariant(optionValue, defaultImageId) {
    const { slideshow } = this.refs;
    if (!slideshow) return;

    const slides = slideshow.refs.slides || [];
    const val_clean = optionValue?.trim()?.toLowerCase();

    let hasAltSpecificSlides = false;
    if (val_clean) {
      hasAltSpecificSlides = slides.some((slide) => {
        const optValue = slide.getAttribute('data-variant-option-value')?.trim()?.toLowerCase();
        return optValue === val_clean;
      });
    }

    const targetSlide = defaultImageId ? slides.find((slide) => slide.getAttribute('slide-id') == defaultImageId) : null;

    for (const slide of slides) {
      const isVariantImage = slide.getAttribute('variant-image') != null || slide.hasAttribute('data-variant-option-value');
      if (!isVariantImage) continue;

      const slideOptValue = slide.getAttribute('data-variant-option-value')?.trim()?.toLowerCase();

      if (hasAltSpecificSlides && slideOptValue) {
        slide.hidden = slideOptValue !== val_clean;
      } else {
        slide.hidden = slide.getAttribute('slide-id') !== defaultImageId;
      }

      if (slide === targetSlide) {
        slide.setAttribute('reveal', '');
        slide.setAttribute('aria-hidden', 'false');
      } else {
        slide.removeAttribute('reveal');
        slide.setAttribute('aria-hidden', slide.hidden ? 'true' : 'false');
      }
    }

    if (defaultImageId && targetSlide) {
      targetSlide.hidden = false;
      if (slideshow.select) {
        slideshow.select({ id: defaultImageId.toString() }, undefined, { animate: false }).catch(() => {});
      }
      // slideshow.select() synchronously strips `reveal` and sets
      // aria-hidden="true" on the previously revealed slide, then early-returns
      // when the requested slide is already the current one (i.e. hovering the
      // already-selected swatch). Re-assert visibility after it runs so the image
      // doesn't blank out in that case (visible in the Carousel layout, where a
      // slide only paints when aria-hidden="false").
      targetSlide.removeAttribute('hidden');
      targetSlide.setAttribute('reveal', '');
      targetSlide.setAttribute('aria-hidden', 'false');
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedOptionValue = this.variantPicker?.selectedOption.value;
    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    this.#showImagesForVariant(selectedOptionValue, selectedImageId);
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }

  /**
   * Previews a variant on swatch hover: shows its image group and morphs in its
   * price (fetched via section rendering, cached per variant). Desktop-only —
   * on touch, `pointerenter` fires on tap and would preview-then-revert the
   * variant the user just picked.
   * @param {string} id - The media id of the variant to preview.
   * @param {Event} [event] - The event that triggered the preview.
   */
  previewVariant(id, event) {
    if (event && 'pointerType' in event && event.pointerType === 'touch') return;
    if (!window.matchMedia('(hover: hover)').matches) return;
    this.#variantPreviewActive = true;
    const { slideshow } = this.refs;

    if (slideshow && id) {
      this.resetVariant.cancel();

      let hoverOptionValue = null;
      if (event && event.target instanceof Element) {
        const label = event.target.closest('label');
        const input = label ? label.querySelector('input[type="radio"]') : null;
        if (input instanceof HTMLInputElement) {
          hoverOptionValue = input.value;
        }
      }

      this.#showImagesForVariant(hoverOptionValue || this.variantPicker?.selectedOption?.value, id);
    }

    if (event && event.target instanceof Element) {
      const label = event.target.closest('label');
      const input = label ? label.querySelector('input[type="radio"]') : null;
      if (input instanceof HTMLInputElement) {
        // Material line preview: swap to the hovered variant's own materials
        const materialLine = this.querySelector('[data-material-line]');
        if (materialLine && input.dataset.materials) {
          setMaterialText(materialLine, input.dataset.materials);
        }

        const variantId = input.dataset.variantId;
        const priceContainer =
          this.querySelectorAll(`product-price [ref='priceContainer']`)[1] ||
          this.querySelector(`product-price [ref='priceContainer']`);

        if (priceContainer && !this.originalPriceHTML) {
          this.originalPriceHTML = priceContainer.outerHTML;
        }

        if (variantId) {
          this.previewPricesCache = this.previewPricesCache || {};
          this.fetchingPreviewVariantId = variantId;

          if (this.previewPricesCache[variantId]) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(this.previewPricesCache[variantId], 'text/html');
            const cachedPriceElement = doc.querySelector(`[ref='priceContainer']`) || doc.body.firstElementChild;
            if (cachedPriceElement && priceContainer) {
              morph(priceContainer, cachedPriceElement);
            }
          } else {
            const productUrl = this.variantPicker?.dataset.productUrl || this.dataset.productUrl;
            if (productUrl) {
              const url = new URL(productUrl, window.location.origin);
              url.searchParams.set('variant', variantId);
              url.searchParams.set('section_id', 'section-rendering-product-card');

              fetch(url.href)
                .then((response) => response.text())
                .then((htmlText) => {
                  const parser = new DOMParser();
                  const doc = parser.parseFromString(htmlText, 'text/html');
                  const newPriceElement = doc.querySelector(`[ref='priceContainer']`) || doc.body.firstElementChild;
                  if (newPriceElement) {
                    this.previewPricesCache[variantId] = newPriceElement.outerHTML;

                    // Only morph if this hover is still the latest one — a slow
                    // response for a swatch the pointer already left must not
                    // overwrite the price of the swatch currently hovered.
                    if (this.fetchingPreviewVariantId === variantId) {
                      const currentPriceContainer =
                        this.querySelectorAll(`product-price [ref='priceContainer']`)[1] ||
                        this.querySelector(`product-price [ref='priceContainer']`);
                      if (currentPriceContainer) {
                        morph(currentPriceContainer, newPriceElement);
                      }
                    }
                  }
                })
                .catch((err) => console.error('Error fetching preview price:', err));
            }
          }
        }
      }
    }
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    // Entering the gallery after a swatch preview: restore the selected variant's price/images
    // first, then advance to the hover image. ONLY after a swatch preview — with nothing previewed
    // this ran a whole second pass over every slide (attribute writes plus its own select(), each
    // with its own geometry reads) on every single card the pointer touched, and left the tree
    // dirty for the read in next() below to flush synchronously.
    if (this.#variantPreviewActive) this.resetVariantInstant();

    slideshow.next(undefined, { animate: false });
    setTimeout(() => this.#preloadNextPreviewImage());
  }

  /**
   * Resets the image when the pointer leaves the card: back to the FIRST slide,
   * wherever the arrows took the gallery. The stock code stepped back exactly ONE
   * slide (previous(), the mirror of the hover's next()) — an arrow-browsed gallery
   * was stranded mid-list — and remembered the browsed position, so the next hover
   * resumed there instead of showing the second image (owner, 29.08; the remembered-
   * position machinery is removed with it).
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!this.variantPicker) {
      if (!slideshow) return;
      slideshow.select(0, undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the card to the selected variant: restores the pre-hover price and
   * shows the selected variant's image group.
   * @param {Event} [event] - The pointer event when triggered from a hover reset.
   */
  /** Whether a swatch preview has altered this card and still needs undoing. */
  #variantPreviewActive = false;

  #resetVariant = (event) => {
    // Taps fire pointerleave too — never revert a variant the user just picked.
    if (event && 'pointerType' in event && event.pointerType === 'touch') return;
    this.#variantPreviewActive = false;
    const { slideshow } = this.refs;

    this.fetchingPreviewVariantId = null;
    if (this.originalPriceHTML) {
      const priceContainer =
        this.querySelectorAll(`product-price [ref='priceContainer']`)[1] ||
        this.querySelector(`product-price [ref='priceContainer']`);
      if (priceContainer) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(this.originalPriceHTML, 'text/html');
        const originalPriceElement = doc.querySelector(`[ref='priceContainer']`) || doc.body.firstElementChild;
        if (originalPriceElement) {
          morph(priceContainer, originalPriceElement);
        }
      }
      this.originalPriceHTML = null;
    }

    this.#updateMaterialLine();

    if (!slideshow) return;

    const selectedOptionValue = this.variantPicker?.selectedOption?.value;
    const selectedImageId = this.variantPicker?.selectedOption?.dataset.optionMediaId;
    const initialSlide = slideshow.initialSlide;
    const slideId = selectedImageId || initialSlide?.getAttribute('slide-id');

    this.#showImagesForVariant(selectedOptionValue, slideId || undefined);
  };

  /**
   * Sets the material line to the selected variant's materials; variants
   * without their own list fall back to the product-level line captured in
   * data-default at render time.
   */
  #updateMaterialLine() {
    const materialLine = this.querySelector('[data-material-line]');
    if (!materialLine) return;

    const checked = /** @type {HTMLInputElement | null} */ (
      this.querySelector('swatches-variant-picker-component input[type="radio"]:checked')
    );
    const materials = checked?.dataset.materials || materialLine.dataset.default;
    if (materials) {
      setMaterialText(materialLine, materials);
    }
  }

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const infiniteResultsList = this.closest('results-list[infinite-scroll="true"]');
    if (!window.Shopify.designMode && infiniteResultsList) {
      // Return ticket in sessionStorage, NEVER in the URL. The old history.replaceState
      // with `?page=N#<md5>` put the hash into the address bar the moment the card was
      // clicked and again while navigating back — no post-load cleanup can hide either
      // flash, and a later reload jumped to the long-left card (owner, 29.08).
      // main-collection.liquid consumes the ticket once on a history return and scrolls
      // to the card; bfcache returns restore scroll natively.
      const parent = this.closest('li');
      try {
        sessionStorage.setItem(
          'r69:return:' + window.location.pathname,
          JSON.stringify({ id: productCardAnchor, page: (parent && parent.dataset.page) || '1' })
        );
      } catch (error) {
        // storage full/blocked — the return simply lands without the scroll
      }
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant immediately, without the hover-leave debounce.
   */
  resetVariantInstant() {
    this.#resetVariant();
  }

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(StandardEvents.productSelect, this.#handleCardProductSelect.bind(this));
  }

  /**
   * Updates the card URL when a variant is selected.
   * @param {ProductSelectEvent} event
   */
  #handleCardProductSelect(event) {
    // Handle URL update via promise resolution
    event.promise
      .then(() => {
        if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
          const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
          currentUrl.searchParams.set('variant', this.pendingVariantId);
          this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
          this.pendingVariantId = null;
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-card] Event promise rejected:', error);
      });
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    // A click commits a new baseline: drop hover-preview price caches on both
    // this picker and the parent card so a later pointerleave restores the
    // newly selected price, not the pre-click one.
    this.originalPriceHTML = null;
    this.fetchingPreviewVariantId = null;
    if (this.parentProductCard instanceof ProductCard) {
      this.parentProductCard.originalPriceHTML = null;
      this.parentProductCard.fetchingPreviewVariantId = null;
    }

    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const hasAvailableVariant = clickedSwatch.dataset.hasAvailableVariant === 'true';
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // Request the first available variant for this swatch.
    if (isSwatchInput && hasAvailableVariant && firstAvailableVariantId) {
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
